import { CodeBlock, Heading, Link, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, VStack } from '@astryxdesign/core';

/**
 * "Handling 429s and quota correctly."
 *
 * The header names, status codes and error codes are the ones the API emits
 * (services/api/internal/middleware/auth.go and the /docs error table); the
 * plan numbers are the catalogue's. The bucket description matches
 * services/api/internal/ratelimit/limiter.go — continuous refill at rpm/60
 * tokens per second up to `burst` — rather than a textbook version of it.
 */

const HEADERS = `HTTP/1.1 200 OK
RateLimit-Limit: 60          # requests per minute on this plan
RateLimit-Remaining: 41      # tokens left in the bucket right now
RateLimit-Reset: 19          # seconds until the bucket is full again
X-Quota-Limit: 50000         # monthly call allowance
X-Quota-Used: 12043          # calls consumed this UTC month

HTTP/1.1 429 Too Many Requests
Retry-After: 2               # only on 429 — wait this many seconds`;

const RETRY = `import time, requests

RETRYABLE = {429, 502, 503}

def forecast(session, payload, attempts=6):
    for attempt in range(attempts):
        r = session.post(f"{API}/v1/forecast", json=payload, timeout=120)
        if r.status_code not in RETRYABLE:
            r.raise_for_status()     # 401/402/403/422 are not retryable — see below
            return r.json()

        # 429 carries the server's own hint. 502/503 do not; back off geometrically,
        # capped, with jitter so a fleet of workers does not retry in lockstep.
        hinted = r.headers.get("Retry-After")
        wait = float(hinted) if hinted else min(30, 0.5 * 2 ** attempt)
        time.sleep(wait + random.uniform(0, 0.25 * wait))

    raise RuntimeError("gave up after repeated retryable failures")`;

const PACING = `# Read the bucket from the last response and pace before the next request
# instead of after a 429. Remaining tokens tell you how much burst is left.
remaining = int(r.headers["RateLimit-Remaining"])
limit     = int(r.headers["RateLimit-Limit"])
if remaining == 0:
    # Bucket is empty: one token arrives every 60/limit seconds.
    time.sleep(60 / limit)`;

const DISCOVER = `model = requests.get(f"{API}/v1/models", headers=H).json()["models"][0]
# {"max_series_per_request": 32, "max_context": 2048, "max_horizon": 128, ...}
# Unmetered, and it reads your plan at call time — so it is right the minute
# after an upgrade, which a constant in your config is not.`;

const ERRORS = [
  { status: '429', code: 'rate_limit_exceeded', retry: 'Yes — after Retry-After', why: 'The token bucket is empty. It refills on its own.' },
  { status: '503', code: 'model_unavailable', retry: 'Yes — seconds', why: 'Cold start; the weights are still loading.' },
  { status: '502', code: 'inference_failed', retry: 'Yes — with backoff', why: 'The backend failed. The call is not billed.' },
  { status: '402', code: 'quota_exceeded', retry: 'No', why: 'The month\'s allowance is spent. Upgrade or wait for the UTC month.' },
  { status: '422', code: 'horizon_limit_exceeded, context_limit_exceeded, series_limit_exceeded', retry: 'No', why: 'The request is larger than the plan allows. `details` carries the ceiling.' },
  { status: '422', code: 'invalid_request', retry: 'No', why: 'Malformed payload. Retrying sends the same malformed payload.' },
  { status: '401', code: 'missing / invalid / revoked_api_key', retry: 'No', why: 'The credential is wrong. Fix the key, not the loop.' },
];

export function ForecastApiRateLimitsArticle() {
  return (
    <>
      <Text>
        A metered API says no in two different ways, and a client that treats them the same will
        either hammer a bucket that is about to refill or patiently retry a quota that will not reset
        for three weeks. The difference is one status code. This post is the retry loop we would want
        every integration to have, the headers it reads, and the table of what is worth retrying.
      </Text>

      <div className="callout">
        <Text weight="medium">The short version</Text>
        <Text color="secondary">
          <span className="mono">429</span> means <em>slow down</em>: sleep for{' '}
          <span className="mono">Retry-After</span> seconds and go again. <span className="mono">402</span>{' '}
          means <em>stop</em>: the monthly quota is gone and no amount of waiting inside this month
          will bring it back. Read <span className="mono">RateLimit-Remaining</span> from every
          response and pace yourself before the 429, not after it.
        </Text>
      </div>

      <VStack gap={2}>
        <Heading level={2} id="two-different-limits">Two limits, two status codes</Heading>
        <Text color="secondary">
          <strong>The rate limit</strong> is per API key, per minute, and it is a token bucket: you
          may spike up to the plan&apos;s burst size, then settle at the sustained rate. Exceeding it
          returns <span className="mono">429 rate_limit_exceeded</span> and a{' '}
          <span className="mono">Retry-After</span> header. The request was not billed and the
          condition clears by itself within seconds.
        </Text>
        <Text color="secondary">
          <strong>The quota</strong> is per account, per UTC calendar month, and it is a counter.
          Exhausting it returns <span className="mono">402 quota_exceeded</span>. Nothing about that
          changes until the month rolls over or the plan does, so a retry is at best wasted and at
          worst a tight loop that hides the real problem from whoever is on call.
        </Text>
        <Table density="compact" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Plan</TableHeaderCell>
              <TableHeaderCell>Requests / minute</TableHeaderCell>
              <TableHeaderCell>Burst</TableHeaderCell>
              <TableHeaderCell>Monthly quota</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              ['Free', 10, 5, '300'],
              ['Starter', 60, 30, '50,000'],
              ['Pro', 300, 120, '500,000'],
              ['Enterprise', 2000, 500, 'unmetered'],
            ].map(([plan, rpm, burst, quota]) => (
              <TableRow key={plan}>
                <TableCell>
                  <Text size="sm">{plan}</Text>
                </TableCell>
                <TableCell>
                  <span className="mono">{rpm}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{burst}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{quota}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="token-bucket">How the token bucket refills</Heading>
        <Text color="secondary">
          Each key has a bucket that holds up to <em>burst</em> tokens and refills continuously at{' '}
          <em>rpm ÷ 60</em> tokens per second. A request takes one token; a request that finds the
          bucket empty is the 429. On Starter that is a bucket of 30 refilling at one token a second:
          you can fire 30 requests instantly, and then one a second forever. The bucket never holds
          more than the burst, so a quiet hour does not bank you a bigger spike later.
        </Text>
        <Text color="secondary">
          The practical consequence is that a steady client almost never sees a 429. A batch job that
          sends one request, waits for the response — which takes over a second on CPU — and sends the
          next is running well under one request a second, and the bucket stays full. The 429s come
          from concurrency: twenty workers that all start at the top of the hour.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="the-headers">The headers on every response</Heading>
        <Text color="secondary">
          Both limits are reported on every response, including successful ones, so a client can see
          a 429 coming and a dashboard can show quota burn without a separate endpoint.
        </Text>
        <div className="code-frame">
          <CodeBlock code={HEADERS} language="http" hasCopyButton container="card" />
        </div>
        <Text size="sm" color="secondary">
          <span className="mono">RateLimit-Reset</span> is seconds until the bucket is <em>full</em>,
          not until the next token arrives. The next token is 60 ÷ limit seconds away whenever the
          bucket is not already full.
        </Text>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="retry-loop">A retry loop that behaves</Heading>
        <Text color="secondary">
          Three properties matter: it honours the server&apos;s hint when there is one, it backs off
          geometrically with jitter when there is not, and it refuses to retry anything that a retry
          cannot fix.
        </Text>
        <div className="code-frame">
          <CodeBlock code={RETRY} language="python" hasCopyButton container="card" />
        </div>
        <Text color="secondary">
          Better than retrying after a 429 is not sending the request that would earn one. The
          remaining-token count is on the previous response; a client that reads it can pace itself.
        </Text>
        <div className="code-frame">
          <CodeBlock code={PACING} language="python" hasCopyButton container="card" />
        </div>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="discover-limits">Discover limits, do not hard-code them</Heading>
        <Text color="secondary">
          Every ceiling — series per request, context window, horizon — is on{' '}
          <span className="mono">GET /v1/models</span>, which is unmetered and reflects the plan at
          the moment of the call. Plan changes apply to the very next request, so a constant copied
          from the pricing page is wrong from the minute you upgrade until the next deploy. The{' '}
          <Link href="/blog/batch-time-series-forecasting-api#chunking">batching post</Link> builds
          its chunk size from this call for exactly that reason.
        </Text>
        <div className="code-frame">
          <CodeBlock code={DISCOVER} language="python" hasCopyButton container="card" />
        </div>
      </VStack>

      <VStack gap={2}>
        <Heading level={2} id="when-retrying-is-wrong">When retrying is the wrong move</Heading>
        <Text color="secondary">
          The loop above retries three codes. Everything else is a message to a human, and the honest
          thing for a client to do is surface it.
        </Text>
        <Table density="compact" dividers="rows">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Code</TableHeaderCell>
              <TableHeaderCell>Retry?</TableHeaderCell>
              <TableHeaderCell>Why</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ERRORS.map((e) => (
              <TableRow key={e.code}>
                <TableCell>
                  <span className="mono">{e.status}</span>
                </TableCell>
                <TableCell>
                  <span className="mono">{e.code}</span>
                </TableCell>
                <TableCell>
                  <Text size="sm">{e.retry}</Text>
                </TableCell>
                <TableCell>
                  <Text size="sm" color="secondary">
                    {e.why}
                  </Text>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Text color="secondary">
          One more case that is not an error at all: a request that succeeds but leaves{' '}
          <span className="mono">X-Quota-Used</span> close to <span className="mono">X-Quota-Limit</span>{' '}
          is the moment to alert, not the 402 that follows. Ninety percent is a reasonable line; the
          dashboard draws the same one.
        </Text>
      </VStack>
    </>
  );
}
